import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { propertyIntakeAPI, realEstateTypeAPI } from '../../services/api';
import { apiErrorMessage } from '../../utils/apiError';
import Modal from '../Common/admin/Modal';
import MapPicker from '../Common/admin/MapPicker';

const EMPTY_UNIT = { unitNumber: '', electricityMeter: '', waterMeter: '' };

// Testing aid, hidden in production builds like the login shortcuts.
const SHOW_SAMPLE = process.env.NODE_ENV !== 'production';

const SAMPLE = {
  form: {
    ownerName: 'عبدالله محمد الأحمد',
    identityNumber: '1010101010',
    dob: '1985-04-12',
    mobileNumber: '0551234567',
    bankName: 'مصرف الراجحي',
    ibanNumber: 'SA0380000000608010167519',
    nameAr: 'برج الياسمين',
    nameEng: 'Jasmine Tower',
    titleDeed: 'TD-2024-00112',
    nationalAddress: 'الرياض - حي العليا - شارع التحلية - 3421',
    latitude: 24.7136,
    longitude: 46.6753,
  },
  units: [
    { unitNumber: 'A-101', electricityMeter: 'E-556677', waterMeter: 'W-112233' },
    { unitNumber: 'A-102', electricityMeter: 'E-556678', waterMeter: 'W-112234' },
    { unitNumber: 'A-201', electricityMeter: 'E-556679', waterMeter: 'W-112235' },
  ],
};

const EMPTY_FORM = {
  ownerName: '',
  identityNumber: '',
  dob: '',
  mobileNumber: '',
  bankName: '',
  ibanNumber: '',
  nameAr: '',
  nameEng: '',
  realEstateTypeId: '',
  titleDeed: '',
  nationalAddress: '',
  latitude: null,
  longitude: null,
};

/**
 * The full "add property" flow in one wide dialog: owner, owner documents,
 * property details with a map-picked location, and any number of units.
 *
 * Laid out in three columns so it stays wide rather than tall.
 */
const PropertyIntakeModal = ({ show, onClose, onSaved, propertyId = null, mode = 'create' }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_FORM);
  const [units, setUnits] = useState([{ ...EMPTY_UNIT }]);
  const [files, setFiles] = useState({ identityImage: null, ibanImage: null, titleDeedImage: null });
  const [types, setTypes] = useState([]);
  const [errors, setErrors] = useState({});
  const [failure, setFailure] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const readOnly = mode === 'view';
  const [unitIds, setUnitIds] = useState([]);

  useEffect(() => {
    if (!show) return;
    setForm(EMPTY_FORM);
    setUnits([{ ...EMPTY_UNIT }]);
    setUnitIds([]);
    setFiles({ identityImage: null, ibanImage: null, titleDeedImage: null });
    setErrors({});
    setFailure(null);

    if (!propertyId) return;

    setLoading(true);
    propertyIntakeAPI
      .getById(propertyId)
      .then(({ data }) => {
        setForm({
          ownerName: data.ownerName || '',
          identityNumber: data.identityNumber || '',
          dob: data.dob ? String(data.dob).slice(0, 10) : '',
          mobileNumber: data.mobileNumber || '',
          bankName: data.bankName || '',
          ibanNumber: data.ibanNumber || '',
          nameAr: data.nameAr || '',
          nameEng: data.nameEng || '',
          realEstateTypeId: data.realEstateTypeId || '',
          titleDeed: data.titleDeed || '',
          nationalAddress: data.nationalAddress || '',
          latitude: data.latitude ?? null,
          longitude: data.longitude ?? null,
        });
        const rows = data.units || [];
        setUnits(rows.length ? rows.map((u) => ({
          unitNumber: u.unitNumber || '',
          electricityMeter: u.electricityMeter || '',
          waterMeter: u.waterMeter || '',
        })) : [{ ...EMPTY_UNIT }]);
        setUnitIds(rows.map((u) => u.id));
      })
      .catch((err) => setFailure(apiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [show, propertyId]);

  useEffect(() => {
    if (!show) return;
    realEstateTypeAPI
      .getAll('', 1, 200)
      .then((res) => setTypes(res.data.items || []))
      .catch((err) => console.error('Failed to load property types:', err));
  }, [show]);

  const set = (name) => (e) => {
    setForm((prev) => ({ ...prev, [name]: e.target.value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const setFile = (name) => (e) =>
    setFiles((prev) => ({ ...prev, [name]: e.target.files?.[0] || null }));

  const setUnit = (index, name) => (e) =>
    setUnits((prev) =>
      prev.map((unit, i) => (i === index ? { ...unit, [name]: e.target.value } : unit))
    );

  const addUnit = () => setUnits((prev) => [...prev, { ...EMPTY_UNIT }]);
  const removeUnit = (index) =>
    setUnits((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));

  const setLocation = useCallback(
    ({ latitude, longitude }) => setForm((prev) => ({ ...prev, latitude, longitude })),
    []
  );

  // Files cannot be pre-filled — browsers forbid setting a file input's value,
  // so the three uploads stay for the user to pick.
  const fillSample = () => {
    setForm((prev) => ({
      ...SAMPLE.form,
      realEstateTypeId: prev.realEstateTypeId || types[0]?.id || '',
    }));
    setUnits(SAMPLE.units.map((unit) => ({ ...unit })));
    setErrors({});
    setFailure(null);
  };

  const submit = async (e) => {
    e.preventDefault();

    const found = {};
    if (!form.ownerName.trim()) found.ownerName = t('tree.required');
    if (!form.nameAr.trim() && !form.nameEng.trim()) found.nameAr = t('tree.required');
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setSaving(true);
    setFailure(null);
    try {
      const body = new FormData();
      body.append(
        'payload',
        JSON.stringify({
          ...form,
          dob: form.dob || null,
          realEstateTypeId: form.realEstateTypeId ? Number(form.realEstateTypeId) : null,
          units: units
            .map((u, i) => ({ ...u, id: unitIds[i] }))
            .filter((u) => u.unitNumber || u.electricityMeter || u.waterMeter),
        })
      );
      Object.entries(files).forEach(([key, file]) => {
        if (file) body.append(key, file);
      });

      if (propertyId) await propertyIntakeAPI.update(propertyId, body);
      else await propertyIntakeAPI.create(body);
      onSaved();
      onClose();
    } catch (err) {
      console.error('Property intake failed:', err);
      setFailure(apiErrorMessage(err));
      setSaving(false);
    }
  };

  const field = (name, labelKey, icon, { type = 'text', required = false } = {}) => (
    <div className="ad-modal-field">
      <label className={`ad-modal-label ${required ? 'ad-label-required' : ''}`}>
        {t(`property.intake.${labelKey}`)}
      </label>
      <span className="ad-input-wrap">
        <i className={`fas ${icon} ad-input-icon`}></i>
        <input
          type={type}
          className={`ad-input-sm ad-input-has-icon ${errors[name] ? 'ad-input-error' : ''}`}
          placeholder={t(`property.intake.${labelKey}`)}
          value={form[name]}
          onChange={set(name)}
          disabled={readOnly}
        />
      </span>
      {errors[name] && <span className="ad-error-text">{errors[name]}</span>}
    </div>
  );

  const fileField = (name, labelKey, icon) => (
    <div className="ad-modal-field">
      <label className="ad-modal-label">{t(`property.intake.${labelKey}`)}</label>
      <span className="ad-input-wrap">
        <input
          type="file"
          className="ad-input-file"
          onChange={setFile(name)}
          disabled={readOnly}
        />
      </span>
      {files[name] && (
        <span className="ad-hint">
          <i className={`fas ${icon}`}></i> {files[name].name}
        </span>
      )}
    </div>
  );

  return (
    <Modal
      show={show}
      title={t(`property.intake.${mode}Title`)}
      size="wide"
      onClose={onClose}
      footer={
        <>
          {SHOW_SAMPLE && !readOnly && (
            <button
              type="button"
              className="ad-btn ad-btn-ghost"
              onClick={fillSample}
              disabled={saving}
            >
              <i className="fas fa-wand-magic-sparkles"></i>
              {t('property.intake.fillSample')}
            </button>
          )}
          <button type="button" className="ad-btn" onClick={onClose} disabled={saving}>
            {readOnly ? t('common.close') : t('common.cancel')}
          </button>
          {!readOnly && (
            <button
              type="submit"
              form="property-intake-form"
              className="ad-btn ad-btn-primary"
              disabled={saving || loading}
            >
              {saving ? t('common.loading') : t('common.save')}
            </button>
          )}
        </>
      }
    >
      <form id="property-intake-form" onSubmit={submit}>
        {failure && <p className="ad-error-text">{failure}</p>}

        <div className="ad-intake-columns">
          {/* --- owner --------------------------------------------------- */}
          <section className="ad-intake-col">
            <h3 className="ad-intake-heading">
              <i className="fas fa-user-tie"></i>
              {t('property.intake.ownerSection')}
            </h3>
            <div className="ad-modal-grid">
              {field('ownerName', 'ownerName', 'fa-user', { required: true })}
              {field('identityNumber', 'identityNumber', 'fa-id-card')}
              {field('dob', 'dob', 'fa-cake-candles', { type: 'date' })}
              {field('mobileNumber', 'mobileNumber', 'fa-mobile-screen')}
              {field('bankName', 'bankName', 'fa-building-columns')}
              {field('ibanNumber', 'ibanNumber', 'fa-money-check')}
            </div>

            <h4 className="ad-intake-subheading">{t('property.intake.ownerDocs')}</h4>
            <div className="ad-modal-grid">
              {fileField('identityImage', 'identityImage', 'fa-id-card')}
              {fileField('ibanImage', 'ibanImage', 'fa-file-invoice')}
            </div>
          </section>

          {/* --- property ------------------------------------------------ */}
          <section className="ad-intake-col">
            <h3 className="ad-intake-heading">
              <i className="fas fa-building"></i>
              {t('property.intake.propertySection')}
            </h3>
            <div className="ad-modal-grid">
              {field('nameAr', 'nameAr', 'fa-building', { required: true })}
              {field('nameEng', 'nameEng', 'fa-building')}

              <div className="ad-modal-field">
                <label className="ad-modal-label">{t('property.intake.propertyType')}</label>
                <span className="ad-input-wrap">
                  <i className="fas fa-house ad-input-icon"></i>
                  <select
                    className="ad-input-sm ad-input-has-icon"
                    value={form.realEstateTypeId}
                    onChange={set('realEstateTypeId')}
                    disabled={readOnly}
                  >
                    <option value="">{t('property.intake.propertyType')}</option>
                    {types.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.nameAr || type.nameEng}
                      </option>
                    ))}
                  </select>
                </span>
              </div>

              {field('titleDeed', 'titleDeed', 'fa-file-lines')}
              {fileField('titleDeedImage', 'titleDeedImage', 'fa-file-image')}
              <div className="ad-modal-field ad-modal-field-full">
                <label className="ad-modal-label">
                  {t('property.intake.nationalAddress')}
                </label>
                <span className="ad-input-wrap">
                  <i className="fas fa-location-dot ad-input-icon"></i>
                  <input
                    className="ad-input-sm ad-input-has-icon"
                    placeholder={t('property.intake.nationalAddress')}
                    value={form.nationalAddress}
                    onChange={set('nationalAddress')}
                    disabled={readOnly}
                  />
                </span>
              </div>
            </div>
          </section>

          {/* --- units --------------------------------------------------- */}
          <section className="ad-intake-col">
            <h3 className="ad-intake-heading">
              <i className="fas fa-layer-group"></i>
              {t('property.intake.unitsSection')}
              <button
                type="button"
                className="ad-column-add"
                onClick={addUnit}
                disabled={readOnly}
                title={t('tree.add')}
              >
                <i className="fas fa-plus"></i>
              </button>
            </h3>

            <div className="ad-unit-rows">
              {units.map((unit, index) => (
                <div key={index} className="ad-unit-row">
                  <span className="ad-unit-index">{index + 1}</span>
                  <input
                    className="ad-input-sm"
                    placeholder={t('property.intake.unitNumber')}
                    value={unit.unitNumber}
                    onChange={setUnit(index, 'unitNumber')}
                    disabled={readOnly}
                  />
                  <input
                    className="ad-input-sm"
                    placeholder={t('property.intake.electricityMeter')}
                    value={unit.electricityMeter}
                    onChange={setUnit(index, 'electricityMeter')}
                    disabled={readOnly}
                  />
                  <input
                    className="ad-input-sm"
                    placeholder={t('property.intake.waterMeter')}
                    value={unit.waterMeter}
                    onChange={setUnit(index, 'waterMeter')}
                    disabled={readOnly}
                  />
                  <button
                    type="button"
                    className="ad-node-action ad-node-action-danger"
                    onClick={() => removeUnit(index)}
                    disabled={readOnly || units.length === 1}
                    title={t('tree.delete')}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* --- location ------------------------------------------------- */}
        <section className="ad-intake-map">
          <h3 className="ad-intake-heading">
            <i className="fas fa-map-location-dot"></i>
            {t('property.intake.locationSection')}
          </h3>
          <MapPicker
            value={{ latitude: form.latitude, longitude: form.longitude }}
            onChange={setLocation}
          />
        </section>
      </form>
    </Modal>
  );
};

export default PropertyIntakeModal;
